/**
 * Web Storage Service
 * Replaces Electron storage with browser localStorage and future API calls
 */

class WebStorageService {
  constructor() {
    this.storage = {
      get: this.getItem.bind(this),
      set: this.setItem.bind(this),
      delete: this.deleteItem.bind(this)
    };
    this.currentUserId = null;
  }

  /**
   * Set current user for data scoping
   * @param {string} userId
   */
  setCurrentUser(userId) {
    this.currentUserId = userId;
  }

  /**
   * Get user-scoped key
   * @param {string} key
   * @returns {string}
   */
  getScopedKey(key) {
    return this.currentUserId ? `${key}_user_${this.currentUserId}` : key;
  }

  /**
   * Get item from localStorage
   * @param {string} key 
   * @returns {Promise<any>}
   */
  async getItem(key) {
    try {
      const scopedKey = this.getScopedKey(key);
      const item = localStorage.getItem(scopedKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Failed to get item from storage:', error);
      return null;
    }
  }

  /**
   * Set item in localStorage
   * @param {string} key 
   * @param {any} value 
   * @returns {Promise<boolean>}
   */
  async setItem(key, value) {
    try {
      const scopedKey = this.getScopedKey(key);
      localStorage.setItem(scopedKey, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Failed to set item in storage:', error);
      return false;
    }
  }

  /**
   * Delete item from localStorage
   * @param {string} key 
   * @returns {Promise<boolean>}
   */
  async deleteItem(key) {
    try {
      const scopedKey = this.getScopedKey(key);
      localStorage.removeItem(scopedKey);
      return true;
    } catch (error) {
      console.error('Failed to delete item from storage:', error);
      return false;
    }
  }

  /**
   * Check if storage is available
   * @returns {boolean}
   */
  isAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Create and export singleton instance
export const webStorageService = new WebStorageService();
export default webStorageService;